import * as React from 'react';
import type { ComponentProps, ContainerRect, ZoomRef } from 'yet-another-react-lightbox';
import { makeUseContext, useController } from 'yet-another-react-lightbox';
import {
  useZoomCallback,
  useZoomImageRect,
  useZoomProps,
  useZoomSensors,
  useZoomState,
} from './hooks';

export type ActiveZoomWrapper = {
  zoomWrapperRef: React.RefObject<HTMLDivElement | null>;
  imageDimensions?: ContainerRect;
};

export type ZoomControllerContextType = ZoomRef & {
  setZoomWrapper: React.Dispatch<React.SetStateAction<ActiveZoomWrapper | undefined>>;
};

export const ZoomControllerContext = React.createContext<ZoomControllerContextType | null>(null);

export const useZoom = makeUseContext('useZoom', 'ZoomControllerContext', ZoomControllerContext);

export function ZoomContextProvider({ children }: ComponentProps) {
  const [zoomWrapper, setZoomWrapper] = React.useState<ActiveZoomWrapper>();

  const { slideRect } = useController();
  const { ref, minZoom, disabled: disabled1 } = useZoomProps();
  const { initialRect, imageRect, maxZoom } = useZoomImageRect(
    slideRect,
    zoomWrapper?.imageDimensions
  );

  const {
    zoom,
    offsetX,
    offsetY,
    disabled: disabled2,
    changeZoom,
    changeOffsets,
    zoomIn,
    zoomOut,
  } = useZoomState(imageRect, maxZoom, zoomWrapper?.zoomWrapperRef);

  const disabled = disabled1 || disabled2;

  useZoomCallback(zoom, disabled);

  useZoomSensors(
    zoom,
    minZoom,
    maxZoom,
    disabled,
    zoomIn,
    zoomOut,
    changeZoom,
    changeOffsets,
    zoomWrapper?.zoomWrapperRef
  );

  const zoomRef = React.useMemo(
    () => ({
      zoom,
      minZoom,
      maxZoom,
      offsetX,
      offsetY,
      disabled,
      zoomIn,
      zoomOut,
      changeZoom,
      imageRect,
      initialZoom: imageRect.height / (initialRect?.height || 1),
    }),
    [
      zoom,
      minZoom,
      maxZoom,
      offsetX,
      offsetY,
      disabled,
      zoomIn,
      zoomOut,
      changeZoom,
      imageRect,
      initialRect?.height,
    ]
  );

  React.useImperativeHandle(ref, () => zoomRef, [zoomRef]);

  const context = React.useMemo(
    () => ({ ...zoomRef, setZoomWrapper }),
    [zoomRef, setZoomWrapper]
  );

  return (
    <ZoomControllerContext.Provider value={context}>{children}</ZoomControllerContext.Provider>
  );
}
